import React, { useState } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { useDestroyDashboardGridRow } from 'api/hooks/use-dashboard-grid-rows'
import ChartCard from './chart-card'
import ConfirmDelete from '../shared/confirm-delete'

function GridRow({
  gridRow,
  dashboardId,
  sectionId,
  params,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  canEdit = false,
  isPreviewMode = false,
  selectedChartId = null,
  onSelectChart = null
}) {
  const [columnPosition, setColumnPosition] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const { column_count, dashboard_charts = [] } = gridRow

  // Row reordering/removal for populated rows now lives in each chart card's menu. But an empty
  // row (no charts, e.g. after deleting its last chart or adding a spare row) has no chart to act
  // on, so it would be stranded on the dashboard forever — surface a delete control just for it.
  const isEmptyRow = dashboard_charts.length === 0

  const { mutate: destroyGridRow } = useDestroyDashboardGridRow({
    dashboardId,
    id: gridRow.id,
    options: {
      onSuccess: () => setShowDeleteConfirm(false)
    }
  })

  const handleClose = () => {
    setColumnPosition(null)
  }

  // Create array of columns based on column_count
  const columns = Array.from({ length: column_count }, (_, index) => {
    const chart = dashboard_charts.find(chart => chart.grid_column_position === index)
    return { position: index, chart }
  })

  const colSize = 12 / column_count

  return (
    <div className="mb-4">
      {canEdit && !isPreviewMode && isEmptyRow && (
        <div className="d-flex justify-content-end mb-2">
          <ConfirmDelete
            id={gridRow.id}
            handleDelete={() => destroyGridRow()}
            message="Are you sure you want to delete this empty row?"
            show={showDeleteConfirm}
            setShow={setShowDeleteConfirm}
          />
        </div>
      )}

      <Row>
        {columns.map(({ position, chart }) => (
        <Col key={position} md={colSize}>
          {chart ? (
            <ChartCard
              chart={chart}
              dashboardId={dashboardId}
              sectionId={sectionId}
              params={params}
              canEdit={canEdit}
              isPreviewMode={isPreviewMode}
              gridRowId={gridRow.id}
              gridColumnPosition={position}
              isSelected={selectedChartId === chart?.id}
              onSelect={onSelectChart}
              onMoveUp={() => onMoveUp && onMoveUp(gridRow)}
              onMoveDown={() => onMoveDown && onMoveDown(gridRow)}
              isFirst={isFirst}
              isLast={isLast}
            />
          ) : (
            canEdit && !isPreviewMode ? (
              <Card className="mb-3" style={{ minHeight: '200px', border: '2px dashed #dee2e6' }}>
                <Card.Body className="d-flex align-items-center justify-content-center">
                  <Button
                    variant="outline-primary"
                    onClick={() => setColumnPosition(position)}
                    className="d-flex align-items-center justify-content-center"
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    <span>Add Component</span>
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <div style={{ minHeight: '200px' }} />
            )
          )}
        </Col>
      ))}
      </Row>

      {columnPosition !== null && canEdit && !isPreviewMode && (
        <ChartCard
          dashboardId={dashboardId}
          sectionId={sectionId}
          params={params}
          chart={null}
          onSaved={handleClose}
          autoOpenModal={true}
          onCancel={handleClose}
          gridRowId={gridRow.id}
          gridColumnPosition={columnPosition}
          canEdit={canEdit}
          isPreviewMode={isPreviewMode}
        />
      )}
    </div>
  )
}

export default GridRow